// Add a product (AdminAddProduct.dc.html): 5 steps, Type, Details, Look, Access and Content, with the
// tile beside them, then "Create as draft". Every type can be made here (Adam, 13 Oct: "I would expect
// to create anything that I want to be able to create"; docs/product-types-plan.md):
// - 7 types to pick; Webinar's tile is a link to Recordings and dates, Webinars, where each webinar is
//   added, since a webinar isn't a product;
// - a type whose pages are drawn in 1 look takes that look (model.js looksFor), and says why;
// - a mastermind is a 12-month membership by default and comes with Group 1; a live cohort with its
//   weeks, Cohort 1 and their sessions; an on-demand course with its modules; a private client space
//   with no prices or terms (Adam opens each client's space in People);
// - its address shows the hub's own login page, and a new linked item links out (1-click join is the
//   Restart Challenge's own route);
// - the pieces list only what the hub does for a new product of the type (no email piece: the hub sends
//   no notification emails, Adam, 5 Oct, stage 6), as a plain list; only the Resources tabs it starts
//   with are tick boxes, since nothing else reads a tick (the review of 13 Oct).
// Prices are mapped by an owner only (price_map is the owner's); an admin is sent to the Price map. An
// owner's new paid product sells through Stripe once it's switched on (its purchases made live here);
// an admin's waits in shadow for the owner. Everything is saved as a draft that nobody sees. Owner and
// admin (the shell).
import { useEffect, useRef, useState } from 'react'
import { PageHeader, Crumbs, Button, ButtonLink, Checkbox, Options, Field, TextInput, DateField, DayField, TimeField, Select, Loading, Problem, useLoad, adminPath, todayInUk, ukToIso, useAdmin } from '../ui/index.js'
import { Link } from '../../lib/router.jsx'
import { longDate, time as timeWords } from '../../lib/format.js'
import { ADD, TYPES } from '../products/copy.js'
import { accessFor, looksFor, runsFor, doneLines, piecesFor, lookColours, slugFor, uniqueSlug, normalHex, isHex, isHttps, isEmail, scheduleSessions, dayName, intervalWords } from '../products/model.js'
import { CATALOGUE_COLUMNS, createProduct, loadUnmapped } from '../products/data.js'
import { LookPicker } from '../products/LookPicker.jsx'
import { Mark } from '../products/bits.jsx'
// the paid tile's kind as the Library names it ("Video", never "Masterclass": decision 44)
import { kindLine } from '../../library/looks.js'
import '../../styles/admin-products.css'

const COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const COVER_MAX = 5 * 1024 * 1024
// every piece, less a Resources tab that starts unticked (a live cohort's weekly pack, as the board has it)
const defaultPieces = type => piecesFor(type).filter(p => p[2] !== 'tab-off').map(p => p[0])
const isTab = p => p[2] === 'tab' || p[2] === 'tab-off'
// the 7 types picked here, in the board's order; Webinar's tile is a link, last
const PICKED = TYPES.filter(t => t.key !== 'webinar')
const WEBINAR = TYPES.find(t => t.key === 'webinar')

function fresh() {
  return {
    step: 0, type: 'live_cohort', name: '', line: '', help: 'course@adamslee.co.uk',
    look: 'master', ownBg: '#FFFFFF', ownAccent: '#0D0D0D', ownText: '#0D0D0D', font: 'Manrope', cover: null,
    access: 'paid', address: '', prices: [], run: 'term', from: 'purchase',
    startOn: '', day: 3, at: '19:00', count: 6, pieces: defaultPieces('live_cohort'),
  }
}

export default function AddProduct({ api, me, role }) {
  const { reloadProducts } = useAdmin()
  const owner = role === 'owner'
  const [f, setF] = useState(fresh)
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const [done, setDone] = useState(null)
  const set = patch => setF(s => ({ ...s, ...patch }))
  const clearError = key => setErrors(e => (e[key] ? { ...e, [key]: undefined } : e))
  const top = useRef(null)
  // every product's slug, so the new address is free (staff read every catalogue row)
  const all = useLoad(() => api.run(api.from('catalogue').select(CATALOGUE_COLUMNS)), [api])
  const access = accessFor(f.type) === 'either' ? f.access : accessFor(f.type)
  const free = access === 'free'
  const taken = (all.data || []).map(p => p.slug)
  const base = slugFor(f.name) || 'your-product'
  const slug = f.name.trim() ? uniqueSlug(base, taken) : base

  // a screen reader hears the new step: focus moves to it on each step, not when the page opens
  const opened = useRef(false)
  useEffect(() => {
    if (!opened.current) { opened.current = true; return }
    top.current?.focus({ preventScroll: true })
  }, [f.step, done])

  // picking a type never refuses: it takes its pieces, a look its pages are drawn in (the 1 picked, where
  // the type allows it), and how its access runs
  function pickType(key) {
    setF(s => {
      const looks = looksFor(key)
      return {
        ...s, type: key, pieces: defaultPieces(key), access: accessFor(key) === 'free' ? 'free' : 'paid',
        look: looks.includes(s.look) ? s.look : looks[0], run: key === 'mastermind' ? 'membership' : 'term',
      }
    })
  }

  function check(step) {
    const e = {}
    if (step >= 1 && !f.name.trim()) e.name = ADD.needName
    if (step >= 1 && f.help.trim() && !isEmail(f.help)) e.help = 'Type an email address, like course@adamslee.co.uk.'
    if (step >= 2 && f.look === 'own' && ![f.ownBg, f.ownAccent, f.ownText].every(isHex)) e.look = ADD.needsHex
    if (step >= 3 && free && f.type === 'linked' && !isHttps(f.address)) e.address = ADD.linkBad
    setErrors(e)
    return e
  }
  // the first step with something to fix, for Create as draft
  const stepOf = e => (e.name || e.help ? 1 : e.look ? 2 : e.address ? 3 : null)

  function next() {
    const e = check(f.step)
    const at = stepOf(e)
    if (at !== null && at <= f.step) { set({ step: at }); return }
    if (f.step < 4) { set({ step: f.step + 1 }); return }
    create()
  }

  async function create() {
    const e = check(4)
    const at = stepOf(e)
    if (at !== null) { set({ step: at }); return }
    setBusy(true)
    setProblem(null)
    try {
      const result = await createProduct(api, { ...f, access }, all.data || [], me?.who?.person_id, { owner })
      reloadProducts?.()
      setDone({ ...result, form: f })
    } catch (err) {
      setProblem(err)
    } finally {
      setBusy(false)
    }
  }

  if (done) return <Done done={done} again={() => { setDone(null); setF(fresh()); setErrors({}); all.reload() }} top={top} />

  const steps = ADD.steps
  return (
    <div className="ap-page ap-add">
      <Crumbs items={[{ label: ADD.crumbs, to: adminPath.products() }, { label: ADD.title }]} />
      <PageHeader title={ADD.title} />
      <nav className="ap-stepper" aria-label={ADD.stepNav}>
        {steps.map(([name, sub], i) => (
          <button key={name} type="button" className="ap-stepper-step" aria-current={i === f.step ? 'step' : undefined}
            data-state={i < f.step ? 'done' : i === f.step ? 'now' : 'todo'} disabled={i > f.step} onClick={() => set({ step: i })}>
            <span className="ap-disc" aria-hidden="true">{i < f.step ? <Mark name="tick" size={14} className="ap-mark ap-mark--heavy" /> : i + 1}</span>
            <span className="ap-stepper-text"><span className="ap-stepper-name">{name}</span><span className="ap-stepper-sub">{sub}</span></span>
          </button>
        ))}
      </nav>
      <p className="ap-stepper-now" aria-hidden="true">{ADD.stepOf(f.step + 1, steps[f.step][0])}</p>

      <div className="ap-add-grid">
        <section className="ap-add-form" aria-labelledby="ap-step-h" tabIndex={-1} ref={top}>
          {f.step === 0 && <TypeStep f={f} pick={pickType} />}
          {f.step === 1 && <DetailsStep f={f} set={set} slug={slug} free={free} clash={Boolean(f.name.trim()) && slug !== base} errors={errors} clearError={clearError} />}
          {f.step === 2 && <LookStep f={f} set={set} error={errors.look} />}
          {f.step === 3 && <AccessStep f={f} set={set} free={free} slug={slug} owner={owner} api={api} error={errors.address} />}
          {f.step === 4 && <ContentStep f={f} set={set} />}
        </section>
        <Tile f={f} free={free} />
      </div>

      {problem && <Problem error={problem} />}
      {all.status === 'error' && <Problem error={all.error} onRetry={all.reload} />}
      <div className="ap-add-foot">
        <Button onClick={() => set({ step: Math.max(0, f.step - 1) })} disabled={f.step === 0 || busy} className="ap-back">{ADD.back}</Button>
        <Button kind="ink" onClick={next} busy={busy} busyLabel={ADD.creating} disabled={all.status === 'loading'}>
          {f.step < 4 ? ADD.next(steps[f.step + 1][0]) : ADD.create}
        </Button>
      </div>
    </div>
  )
}

function StepHead({ title, line }) {
  return (
    <div className="ap-step-head">
      <h2 className="ap-step-h" id="ap-step-h">{title}</h2>
      {line && <p className="ap-step-sub">{line}</p>}
    </div>
  )
}

// The 7 types as radios, then Webinar as a link in the grid's last cell, outside the radiogroup: a
// webinar is added in Recordings and dates, Webinars, not made here
function TypeStep({ f, pick }) {
  return (
    <>
      <StepHead title={ADD.typeH2} line={ADD.typeLine} />
      <div className="ap-types-grid">
        <div className="ap-types-radios" role="radiogroup" aria-label={ADD.typeH2}>
          {PICKED.map(t => (
            <button key={t.key} type="button" role="radio" aria-checked={f.type === t.key}
              className="ap-opt ap-type-opt" onClick={() => pick(t.key)}>
              <span className="ap-opt-name">{t.name}</span>
              <span className="ap-opt-what">{t.add || t.what}</span>
              <span className="ap-opt-foot"><span>{t.like}</span><span className="ap-opt-access">{t.access}</span></span>
            </button>
          ))}
        </div>
        <Link to={adminPath.webinars()} className="ap-opt ap-type-opt ap-type-link">
          <span className="ap-opt-name">{WEBINAR.name}</span>
          <span className="ap-opt-what">{WEBINAR.what}</span>
          <span className="ap-type-go">{WEBINAR.link}</span>
          <span className="ap-opt-foot"><span>{WEBINAR.like}</span><span className="ap-opt-access">{WEBINAR.access}</span></span>
        </Link>
      </div>
    </>
  )
}

function DetailsStep({ f, set, slug, free, clash, errors, clearError }) {
  // "Give it a name first." goes as soon as a name is typed, not at the next Next (item 205)
  const named = v => { set({ name: v }); if (v.trim()) clearError('name') }
  return (
    <>
      <StepHead title={ADD.detailsH2} line={ADD.detailsLine} />
      <Field label={ADD.name} plain error={errors.name}>{p => <TextInput {...p} value={f.name} onChange={v => named(v)} placeholder={ADD.namePlaceholder} maxLength={120} />}</Field>
      <Field label={ADD.line} plain>{p => <TextInput {...p} value={f.line} onChange={v => set({ line: v })} placeholder={ADD.linePlaceholder} maxLength={120} />}</Field>
      <div className="ad-field">
        <span className="ap-flabel">{ADD.address}</span>
        <span className="ap-readonly">{free ? 'learn.adamslee.co.uk/free-resources' : `learn.adamslee.co.uk/${slug}`}</span>
        {clash && !free && <p className="ad-hint">{ADD.addressTaken}</p>}
      </div>
      <Field label={ADD.questions} plain error={errors.help}>{p => <TextInput {...p} type="email" value={f.help} onChange={v => set({ help: v })} />}</Field>
    </>
  )
}

function LookStep({ f, set, error }) {
  const input = useRef(null)
  const [coverError, setCoverError] = useState(null)
  const pickFile = e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!COVER_TYPES.includes(file.type)) { setCoverError(ADD.coverBadType); return }
    if (file.size > COVER_MAX) { setCoverError(ADD.coverTooBig); return }
    setCoverError(null)
    set({ cover: file })
  }
  return (
    <>
      <StepHead title={ADD.lookH2} line={ADD.lookLine} />
      <LookPicker value={f} onChange={set} looks={looksFor(f.type)} fixed={ADD.lookFixed[f.type] || null} />
      {error && <p className="ad-error" role="alert">{error}</p>}
      <div className="ap-cover-row">
        <input ref={input} type="file" accept={COVER_TYPES.join(',')} className="ap-file" tabIndex={-1} aria-hidden="true" onChange={pickFile} />
        <Button size="sm" className="ap-btn-40" onClick={() => input.current?.click()}>{f.cover ? ADD.coverAdded(f.cover.name) : ADD.coverAdd}</Button>
        <span className="ap-step-line">{ADD.coverLine}</span>
      </div>
      {coverError && <p className="ad-error" role="alert">{coverError}</p>}
    </>
  )
}

function AccessStep({ f, set, free, slug, owner, api, error }) {
  const either = accessFor(f.type) === 'either'
  const fixed = accessFor(f.type)
  return (
    <>
      <StepHead title={ADD.accessH2} line={ADD.accessLine} />
      <div className="ap-two" role="radiogroup" aria-label={ADD.accessH2}>
        {[['paid', ADD.paid], ['free', ADD.freeOpt]].map(([key, [name, what]]) => {
          const off = !either && fixed !== key
          return (
            <button key={key} type="button" role="radio" aria-checked={(free ? 'free' : 'paid') === key} aria-disabled={off || undefined}
              className={`ap-opt${off ? ' ap-opt--greyed' : ''}`} onClick={() => { if (!off) set({ access: key }) }}>
              <span className="ap-opt-name">{name}</span><span className="ap-opt-what">{what}</span>
            </button>
          )
        })}
      </div>
      {f.type === 'client_space'
        ? <p className="ap-step-line">{ADD.accessNote.client_space}</p>
        : (
          <>
            <p className="ap-step-line">{either ? ADD.accessNote.either : fixed === 'free' ? ADD.accessNote.free : ADD.accessNote.paid}</p>
            {free ? <FreeAccess f={f} set={set} error={error} /> : <PaidAccess f={f} set={set} slug={slug} owner={owner} api={api} />}
          </>
        )}
    </>
  )
}

// A free item: a pack downloads from the hub; a linked item opens its own address (1-click join is the
// Restart Challenge's own route, so a new 1 links out)
function FreeAccess({ f, set, error }) {
  const opts = f.type === 'resource_pack'
    ? [{ value: 'download', label: ADD.download[0], note: ADD.download[1] }]
    : [{ value: 'link', label: ADD.linkOut[0], note: ADD.linkOut[1] }]
  return (
    <div className="ap-sub">
      <span className="ap-flabel" id="ap-how">{ADD.howH3}</span>
      <Options labelledBy="ap-how" value={opts[0].value} options={opts} className="ap-join-opts" />
      {f.type === 'linked' && <p className="ad-hint">{ADD.linkOnly}</p>}
      {f.type === 'linked' && (
        <Field label={ADD.linkAddress} plain error={error}>{p => <TextInput {...p} type="url" inputMode="url" value={f.address} onChange={v => set({ address: v })} placeholder={ADD.linkPlaceholder} />}</Field>
      )}
      <p className="ap-step-line">{ADD.freeLine}</p>
    </div>
  )
}

function PaidAccess({ f, set, slug, owner, api }) {
  const prices = useLoad(() => (owner ? loadUnmapped(api) : []), [owner])
  const ticked = id => f.prices.some(p => p.id === id)
  const toggle = price => set({ prices: ticked(price.id) ? f.prices.filter(p => p.id !== price.id) : [...f.prices, price] })
  return (
    <>
      <div className="ap-sub">
        <span className="ap-flabel">{ADD.pricesH3}</span>
        {!owner && <p className="ap-step-line">{ADD.pricesAdmin}</p>}
        {owner && prices.status === 'loading' && <Loading>{ADD.pricesLoading}</Loading>}
        {owner && prices.status === 'error' && <Problem error={prices.error} onRetry={prices.reload} />}
        {owner && prices.status === 'ready' && prices.data.length === 0 && <p className="ap-step-line">{ADD.pricesNone}</p>}
        {owner && prices.status === 'ready' && prices.data.map(p => (
          <Checkbox key={p.id} className="ap-tick" checked={ticked(p.id)} onChange={() => toggle(p)}>{ADD.priceName(p, intervalWords(p.interval))}</Checkbox>
        ))}
        {owner && <p className="ad-hint">{ADD.pricesNote}</p>}
      </div>
      <div className="ap-sub">
        <span className="ap-flabel" id="ap-runs">{ADD.runsH3}</span>
        <Options labelledBy="ap-runs" value={f.run} onChange={v => set({ run: v })} options={ADD.runs.filter(r => runsFor(f.type).includes(r.value))} className="ap-runs" />
        {f.run === 'term' && (
          <div className="ap-sub ap-sub--tight">
            <span className="ap-flabel" id="ap-from">{ADD.runsFrom}</span>
            <Options labelledBy="ap-from" row value={f.from} onChange={v => set({ from: v })}
              options={[{ value: 'purchase', label: 'The purchase' }, { value: 'access_start', label: 'When their access starts' }]} />
          </div>
        )}
      </div>
      <p className="ap-step-line ap-login-line">{ADD.loginLine(slug)}</p>
    </>
  )
}

const WEEK_COUNTS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))

function ContentStep({ f, set }) {
  const scheduled = f.type === 'live_cohort'
  const pieces = piecesFor(f.type)
  const plan = scheduled ? scheduleSessions({ startOn: f.startOn, day: f.day, at: f.at, count: f.count }, ukToIso) : []
  const toggle = key => set({ pieces: f.pieces.includes(key) ? f.pieces.filter(k => k !== key) : [...f.pieces, key] })
  const line = scheduled ? ADD.contentScheduled : f.type === 'mastermind' ? ADD.contentMastermind : ADD.contentPieces
  // what comes with every product of the type, listed; the Resources tabs it starts with, ticked
  const listed = pieces.filter(p => !isTab(p))
  const tabs = pieces.filter(isTab)
  return (
    <>
      <StepHead title={ADD.contentH2} line={line} />
      {f.type === 'on_demand' && (
        <div className="ap-sub">
          <div className="ap-schedule ap-schedule--modules">
            <Field label={ADD.modules} plain>{p => <Select {...p} value={String(f.count)} onChange={v => set({ count: Number(v) })} options={WEEK_COUNTS} />}</Field>
          </div>
          <p className="ap-step-line">{ADD.modulesLine}</p>
        </div>
      )}
      {scheduled && (
        <>
          <div className="ap-schedule">
            <DateField label={ADD.cohortStarts} plain value={f.startOn} min={todayInUk()} onChange={v => set({ startOn: v })} />
            <DayField label={ADD.day} plain value={f.day} onChange={v => set({ day: v })} />
            <TimeField label={ADD.time} plain value={f.at} onChange={v => set({ at: v })} />
            <Field label={ADD.weeks} plain>{p => <Select {...p} value={String(f.count)} onChange={v => set({ count: Number(v) })} options={WEEK_COUNTS} />}</Field>
          </div>
          <p className="ap-step-line" aria-live="polite">
            {plan.length ? ADD.scheduleLine(longDate(plan[0].starts_at), dayName(f.day), timeWords(plan[0].starts_at), f.count) : ADD.scheduleSkip}
          </p>
        </>
      )}
      {listed.length > 0 && (
        <div className="ap-sub">
          <span className="ap-flabel" id="ap-inside">{f.type === 'live_cohort' ? ADD.eachWeek : ADD.inside}</span>
          <ul className="ap-pieces" aria-labelledby="ap-inside">
            {listed.map(([key, label]) => (
              <li key={key} className="ap-piece"><Mark name="tick" size={14} className="ap-mark ap-mark--bold" />{label}</li>
            ))}
          </ul>
        </div>
      )}
      {tabs.length > 0 && (
        <div className="ap-sub">
          <span className="ap-flabel">{ADD.tabsH3}</span>
          {tabs.map(([key, label]) => (
            <Checkbox key={key} className="ap-tick" checked={f.pieces.includes(key)} onChange={() => toggle(key)}>
              {label}
            </Checkbox>
          ))}
          <p className="ad-hint">{ADD.tabsLine}</p>
        </div>
      )}
    </>
  )
}

function Tile({ f, free }) {
  const own = { bg: normalHex(f.ownBg), accent: normalHex(f.ownAccent), text: normalHex(f.ownText), heading_font: f.font }
  const c = lookColours(f.look, f.look === 'own' ? { ...own, bg: isHex(own.bg) ? own.bg : '#FFFFFF', accent: isHex(own.accent) ? own.accent : '#0D0D0D', text: isHex(own.text) ? own.text : '#0D0D0D' } : null)
  const t = TYPES.find(x => x.key === f.type)
  const name = f.name.trim() || ADD.tileName
  return (
    <aside className="ap-tile-col" aria-label={ADD.tileH3}>
      <span className="ap-tile-h" aria-hidden="true">{ADD.tileH3}</span>
      <div className="ap-tile-frame">
        <div className="ap-tile">
          <div className="ap-tile-art" style={{ background: c.bg }}>
            <span className="ap-tile-rule" style={{ background: c.accent }} />
            <span className="ap-tile-name" style={{ color: c.text, fontFamily: c.font, fontWeight: c.weight }}>{name}</span>
          </div>
          <div className="ap-tile-foot">
            <span className="ap-tile-kind">{free ? `${t.name}, free` : kindLine({ type: f.type })}</span>
            <span className="ap-tile-title">{name}</span>
            <span className="ap-tile-line">{f.line.trim() || ADD.tileLine}</span>
          </div>
        </div>
      </div>
      <p className="ap-tile-note">{free ? ADD.tileNote.free : ADD.tileNote.paid}</p>
    </aside>
  )
}

function Done({ done, again, top }) {
  const { made } = done
  const lines = doneLines(done)
  return (
    <div className="ap-page ap-add">
      <Crumbs items={[{ label: ADD.crumbs, to: adminPath.products() }, { label: ADD.title }]} />
      <PageHeader title={ADD.title} />
      <section className="ap-done" aria-labelledby="ap-done-h" tabIndex={-1} ref={top}>
        <span className="ap-done-mark" aria-hidden="true"><Mark name="tick" size={22} className="ap-mark ap-mark--heavy" /></span>
        <h2 className="ap-done-h" id="ap-done-h">{ADD.doneTitle(made.name)}</h2>
        {lines.map(l => <p className="ap-done-line" key={l}>{l}</p>)}
        <div className="ap-done-actions">
          <ButtonLink to={adminPath.products()} kind="ink">{ADD.goProducts}</ButtonLink>
          <ButtonLink to={adminPath.product(made.slug)}>{ADD.settings}</ButtonLink>
          <Button onClick={again}>{ADD.addAnother}</Button>
        </div>
      </section>
    </div>
  )
}
